import { CurriculumAdapter, LevelLite, LevelStatus } from './types';
import RealOrAiThumb from '../../../shared/assets/images/games/Real Or AI Thumbnail.png';

/**
 * Overture: Create Worlds with AI (2026)
 * - 12 classes / 12 levels
 * - Responsible, teacher-mediated prompting workflow
 *
 * Notes:
 * - Do NOT embed any login credentials here. Keep links + workflow only.
 * - Keep mission ids stable; they are used in URLs and saved progress.
 */

const GEMINI_LINK = 'https://gemini.google.com/app';
const SUNO_LINK = 'https://suno.com/home';
const MESHY_LINK = 'https://www.meshy.ai/workspace';
const ROSEBUD_LINK = 'https://www.rosebud.ai/';
const FAST3D_LINK = 'https://fast3d.io/';
const SKYBOX_LINK = 'https://skybox.blockadelabs.com/';
const OVERTURE_LINK = 'https://overturegames.org/';

const SAFETY_IMAGE_PROMPT =
  'You are an AI image generator being used by elementary school students in a classroom. Only create images that are safe, kind, and appropriate for children. Do not generate content that is mean, embarrassing, bullying, or targets a real person or student by name. Do it in a cartoon style. Do not use a hyper-realistic style. Do not create poop, gross-out, shock, or horror content. Do not include violence, sexual content, drugs, scary imagery, or anything inappropriate for kids. If a request violates these rules, do not generate the image.';

const SAFETY_MUSIC_PROMPT =
  'You are an AI music generator being used by elementary school students in a classroom. Only create songs, melodies, and lyrics that are safe, kind, and appropriate for children. Social Safety: Do not generate audio or lyrics that are mean, embarrassing, bullying, or target a real person or student by name. Musical Style: Ensure the music has a playful, upbeat, whimsical, or educational tone. Avoid aggressive, dark, or overly intense musical genres that might be unsettling. Content Restrictions: Do not create lyrics or sounds involving gross-out humor, shock value, or horror themes. Prohibited Themes: Do not include references to violence, sexual content, drugs, scary sounds, or any language/themes inappropriate for children. If a request violates these rules, do not generate the audio.';

type MissionLite = {
  type: 'completion' | 'measurable';
  id: string;
  text: string;
  completed?: boolean;
  gameId?: string;
  // measurable:
  target?: number;
  current?: number;
  unit?: string;
};

const reflectMission = (levelId: string): MissionLite => ({
  type: 'measurable',
  id: `reflect-and-progress-${levelId}`,
  text: 'Reflect and Progress',
  target: 1,
  current: 0,
  unit: 'students',
});

const LEVELS: LevelLite[] = [
  {
    id: '1',
    title: 'Class 1: Character Creators',
    kind: 'required',
    briefSummary:
      'Teams draw characters on paper; you photograph them; AI versions reveal next week.',
    teacherNotes:
      'Goal: every team leaves with at least one character image through the teacher-filtered Gemini workflow.\n\nBefore class: open Gemini, paste the safety prompt into a fresh chat, open the Canva artifacts board, and stage Character Creation templates.\n\nFacilitation: pair quieter kids with talkers; keep WRITER/PROMPTER roles clear; preview every AI result before projecting.\n\nIf teams finish early: add a secret item (L3) or revise mood/pose. If running behind: skip Reflect discussion and send Board Mode reflections only.',
    missions: [
      { type: 'completion', id: 'cwai-1-welcome', text: 'Welcome & Overview (5 min)', completed: false },
      { type: 'completion', id: 'cwai-1-realorai', text: 'Warm-Up: RealOrAI — Level 1 (10 min)', completed: false, gameId: 'real-or-ai' },
      { type: 'completion', id: 'cwai-1-setup', text: 'Character Creators Setup (10 min)', completed: false },
      { type: 'completion', id: 'cwai-1-character-l1', text: 'Level 1: Describe Your Character (5 min)', completed: false },
      { type: 'completion', id: 'cwai-1-character-l2', text: 'Level 2: Dress to Impress (5 min)', completed: false },
      { type: 'completion', id: 'cwai-1-character-l3', text: 'Level 3: Secret Items (5 min)', completed: false },
      { type: 'completion', id: 'cwai-1-capture', text: 'Photograph team drawings (5 min)', completed: false },
      reflectMission('1'),
    ],
  },
  {
    id: '2',
    title: 'Class 2: World Builders',
    kind: 'required',
    teacherNotes:
      'Goal: teams describe a world (land, weather, mood) and generate at least one world image.\n\nWarm up with RealOrAI L2/L3, then move into landscape → plants/animals → buildings. Keep the teacher-filtered workflow.\n\nIf teams finish early: “Make a Friend” collab (L4). Watch for overlong prompts — coach full sentences, not word lists.',
    missions: [
      { type: 'completion', id: 'cwai-2-review', text: 'Review (5 min)', completed: false },
      { type: 'completion', id: 'cwai-2-realorai', text: 'Warm-Up: RealOrAI — Level 2 & 3 (10 min)', completed: false, gameId: 'real-or-ai' },
      { type: 'completion', id: 'cwai-2-world-l1', text: 'Level 1: Describe Your Landscape (10 min)', completed: false },
      { type: 'completion', id: 'cwai-2-world-l2', text: 'Level 2: Plants & Animals (10 min)', completed: false },
      { type: 'completion', id: 'cwai-2-world-l3', text: 'Level 3: Buildings (10 min)', completed: false },
      { type: 'completion', id: 'cwai-2-world-l4', text: 'Level 4: Make a Friend (5 min collab)', completed: false },
      reflectMission('2'),
    ],
  },
  {
    id: '3',
    title: 'Class 3: Story Tellers',
    kind: 'required',
    teacherNotes:
      'Goal: beginning → middle → end story structure with clear prompts.\n\nStart with a whole-class story warm-up so kids hear strong examples. Then teams draft beginning, conflict, resolution.\n\nCoach: who / where / problem / feeling. If early finishers: revise one sentence to make the AI result clearer.',
    missions: [
      { type: 'completion', id: 'cwai-3-review', text: 'Review (5 min)', completed: false },
      { type: 'completion', id: 'cwai-3-warmup-class-story', text: 'Warm-Up: Whole Class Story (10 min)', completed: false },
      { type: 'completion', id: 'cwai-3-beginning', text: 'Level 1: Beginning (Introduction) (10 min)', completed: false },
      { type: 'completion', id: 'cwai-3-middle', text: 'Level 2: Middle (Conflict) (10 min)', completed: false },
      { type: 'completion', id: 'cwai-3-end', text: 'Level 3: End (Resolution) (10 min)', completed: false },
      reflectMission('3'),
    ],
  },
  {
    id: '4',
    title: 'Class 4: Video Producers',
    kind: 'required',
    missions: [
      { type: 'completion', id: 'cwai-4-review', text: 'Review (5 min)', completed: false },
      { type: 'completion', id: 'cwai-4-watch-example', text: 'Warm-Up: Watch Bao + Discussion (5–10 min)', completed: false },
      { type: 'completion', id: 'cwai-4-write-video-prompt', text: 'Make your film: Styles, Moods & Techniques (10 min)', completed: false },
      { type: 'completion', id: 'cwai-4-generate-video', text: 'Put it all together (10 min)', completed: false },
      { type: 'completion', id: 'cwai-4-download-upload', text: 'Download + upload videos (after class)', completed: false },
      reflectMission('4'),
    ],
  },
  {
    id: '5',
    title: 'Class 5: 3D Model Animators',
    kind: 'required',
    missions: [
      { type: 'completion', id: 'cwai-5-review', text: 'Introduction Activity: Review last class (10 min)', completed: false },
      { type: 'completion', id: 'cwai-5-teams-fave-characters', text: 'Teams: favorite animated movies + characters (10 min)', completed: false },
      { type: 'completion', id: 'cwai-5-class-demo', text: 'Full Class Character Animation (25 min)', completed: false },
      { type: 'completion', id: 'cwai-5-teams-tpose', text: 'Group Character Animations (Cliff Hanger) (15 min)', completed: false },
      { type: 'completion', id: 'cwai-5-extra-fast3d', text: 'Extra Activity: Hands on Generation (15 min)', completed: false },
      reflectMission('5'),
    ],
  },
  {
    id: '6',
    title: 'Class 6: Musical Composers',
    kind: 'required',
    missions: [
      { type: 'completion', id: 'cwai-6-welcome', text: 'Welcome & Overview (5 min)', completed: false },
      { type: 'completion', id: 'cwai-6-warmup-rocket-race', text: 'Warm-Up: Rocket Race (15 min)', completed: false, gameId: 'rocket-race' },
      { type: 'completion', id: 'cwai-6-level1-about', text: 'Level 1: What is it about? (10 min)', completed: false },
      { type: 'completion', id: 'cwai-6-level2-lyrics', text: 'Level 2: Lyrics (10 min)', completed: false },
      { type: 'completion', id: 'cwai-6-level3-style', text: 'Level 3: Style (10 min)', completed: false },
      { type: 'completion', id: 'cwai-6-generate-song', text: 'Generate song (teacher-filtered)', completed: false },
      { type: 'completion', id: 'cwai-6-download-upload', text: 'Download + upload mp3 files', completed: false },
      reflectMission('6'),
    ],
  },
  {
    id: '7',
    title: 'Class 7: Game Developers',
    kind: 'required',
    missions: [
      { type: 'completion', id: 'cwai-7-review', text: 'Review (5 min)', completed: false },
      { type: 'completion', id: 'cwai-7-analyze-games', text: 'Warm-Up: Analyze Overture Music Games (20 min)', completed: false },
      { type: 'completion', id: 'cwai-7-level1-context', text: 'Level 1: Context (10 min)', completed: false },
      { type: 'completion', id: 'cwai-7-level2-problem-goal', text: 'Level 2: Problem & Goal (10 min)', completed: false },
      { type: 'completion', id: 'cwai-7-level3-put-together', text: 'Level 3: Put it all together (10 min)', completed: false },
      { type: 'completion', id: 'cwai-7-generate-game', text: 'Generate game (Rosebud.ai)', completed: false },
      { type: 'completion', id: 'cwai-7-share-record', text: 'Artifacts: share link + screen recording', completed: false },
      reflectMission('7'),
    ],
  },
  {
    id: '8',
    title: 'Class 8: Boss Builders (Bonus)',
    kind: 'optional',
    missions: [],
  },
  {
    id: '9',
    title: 'Class 9: Sound Designers (Bonus)',
    kind: 'optional',
    missions: [],
  },
  {
    id: '10',
    title: 'Class 10: Playtesters (Bonus)',
    kind: 'optional',
    missions: [],
  },
  {
    id: '11',
    title: 'Class 11: Customizations (Bonus)',
    kind: 'optional',
    missions: [],
  },
  {
    id: '12',
    title: 'Class 12: Showcase & Celebration',
    kind: 'flex',
    missions: [],
  },
];

const indexById = (id: string) => LEVELS.findIndex(l => l.id === id);

const getLevelStatus = (levelId: string, currentLevelId: string, completedIds: string[]): LevelStatus => {
  if (completedIds.includes(levelId)) return 'completed';
  if (levelId === currentLevelId) return 'next';
  return 'upcoming';
};

const getShortLevelName = (title: string) => (title.length > 12 ? `${title.slice(0, 12)}…` : title);

type MaterialItem = string | { label: string; link: string };
type InstructionItem = string | { text: string; details?: string[] };

const baseActivity = (missionId: string) => ({
  id: `ai-2026-create-worlds-${missionId}`,
  title: 'Activity',
  description: '',
  image: '',
  duration: '5–15 minutes',
  difficulty: 'Easy',
  materials: [] as MaterialItem[],
  instructions: [] as InstructionItem[],
});

/** Printable Character Creation sheet — kids copy onto blank paper when projecting. */
const CHARACTER_CREATION_WORKSHEET = {
  title: 'Character Creation',
  printHtml: `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Character Creation</title>
<style>
  body{font-family:Georgia,serif;max-width:720px;margin:24px auto;padding:0 20px;color:#1a1a1a}
  h1{font-size:28px;margin:0 0 8px}
  .sub{color:#555;margin:0 0 24px;font-size:14px}
  .field{border:1.5px solid #222;border-radius:8px;padding:14px 16px;margin-bottom:14px;min-height:52px}
  .label{font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#555;margin-bottom:6px}
  .line{border-bottom:1px solid #bbb;height:28px;margin-top:8px}
  .hint{font-size:13px;color:#666;margin-top:6px}
  @media print{body{margin:12px}}
</style></head><body>
<h1>Character Creation</h1>
<p class="sub">Write in complete sentences. Your words become your character.</p>
<div class="field"><div class="label">What animal are they?</div><div class="line"></div></div>
<div class="field"><div class="label">What’s their name?</div><div class="line"></div></div>
<div class="field"><div class="label">What are they doing?</div><div class="line"></div></div>
<div class="field"><div class="label">How do they feel?</div><div class="line"></div></div>
<div class="field"><div class="label">Final prompt (one or two full sentences)</div>
<div class="line"></div><div class="line"></div>
<p class="hint">Example: Make an image of my character. My character is a zebra named Zed. Zed is dancing and happy.</p>
</div>
</body></html>`,
};

const getMissionActivity = (missionId: string) => {
  const entries: Record<string, any> = {
    // Shared
    'cwai-1-realorai': {
      title: 'Warm-Up: Real or AI?',
      missionType: 'warm-up',
      priority: 'flex',
      plannedMinutes: 10,
      prompt: 'Is it real or AI? Look closely — what clue gives it away?',
      image: RealOrAiThumb,
      link: '/games/RealOrAI/index.html',
      gameId: 'real-or-ai',
      duration: '10 minutes',
      script: [
        { kind: 'do', text: 'While the game loads, ask each student their name and favorite ice cream.' },
        {
          kind: 'say',
          text: 'AI is a computer program that predicts what comes next based on a prompt. It uses tons of data it was trained on.',
        },
        { kind: 'do', text: 'Play one round of RealOrAI together on the projector.' },
        { kind: 'say', text: 'What clue made you think it was real or AI?' },
        {
          kind: 'say',
          text: 'Your words control the output. Full sentences help the AI follow you — that’s what we’ll practice today.',
        },
      ],
    },
    'cwai-2-realorai': {
      title: 'Warm-Up: Real or AI?',
      description:
        'Play a new round of RealOrAI (Level 2 & 3) and connect “prompt → output”. Remind students how AI predicts from prompts.',
      image: RealOrAiThumb,
      link: '/games/RealOrAI/index.html',
      duration: '10 minutes',
      materials: [{ label: 'Overture', link: OVERTURE_LINK }],
      instructions: [
        'Play 1 round together.',
        'Remind: “AI predicts what comes next from a prompt, using data it was trained on.”',
        'Ask students to explain their reasoning in a full sentence.',
        'Point out “AI tells” and ask what evidence they used.',
      ],
    },
    'cwai-6-warmup-rocket-race': {
      title: 'Warm-Up: Rocket Race (Instruments)',
      description:
        'Play 2–3 rounds of Rocket Race (instruments easy and instruments hard). Help students identify instruments they can add to their song.',
      duration: '10–15 minutes',
      difficulty: 'Easy',
      materials: [{ label: 'Overture', link: OVERTURE_LINK }],
      instructions: [
        'Play 2–3 rounds of Rocket Race: instruments easy, then instruments hard.',
        'Ask: “What instruments did you hear?”',
        'Ask: “How did the music feel? Happy? Calm? Fast? Slow?”',
        'Optional: play one example song from the teacher toolbelt music tab (with lyrics) and ask students to sing a line they know.',
      ],
    },

    // Reflect & Progress (dynamic count handled by NextClassSection)
    'reflect-and-progress-1': {
      title: 'Reflect and Progress',
      missionType: 'reflect',
      priority: 'flex',
      plannedMinutes: 5,
      prompt: 'What details did you give the AI that helped your character look right?',
      duration: '5 minutes',
      script: [
        { kind: 'do', text: 'Open Board Mode and send reflections to present students.' },
        { kind: 'do', text: 'Wait until reflections are in, then invite one person from each team up front.' },
        { kind: 'say', text: 'What is your character?' },
        {
          kind: 'say',
          text: 'What details did you give the AI that helped it make your character look right?',
        },
        {
          kind: 'say',
          text: 'What happened when the AI didn’t have enough information — or when you added more?',
        },
        {
          kind: 'say',
          text: 'If you could change one sentence in your prompt to make your image better, what would you change and why?',
        },
      ],
    },
    'reflect-and-progress-2': {
      title: 'Reflect and Progress',
      description: 'Reflection + discussion on iteration and detail.',
      duration: '5 minutes',
      difficulty: 'Easy',
      instructions: [
        'Open Board Mode and send reflections.',
        'Discussion prompts:',
        '“What changed as you added details?”',
        '“How did your words control the AI?”',
        '“What makes a prompt safe and clear?”',
        'Invite a few students to share their favorite world or character.',
      ],
    },
    'reflect-and-progress-3': {
      title: 'Reflect and Progress',
      description: 'Reflection + discussion on story structure, context, and revision.',
      duration: '5–8 minutes',
      difficulty: 'Easy',
      instructions: [
        'Open Board Mode and send reflections.',
        'Discussion prompts:',
        '“What happened in the beginning, middle, and end of your story?”',
        '“What details helped the AI make your pictures look right?”',
        '“What happened when the AI didn’t have enough information—or when you added more?”',
        '“If you could change one sentence in your prompt to make your story better, what would you change and why?”',
      ],
    },
    'reflect-and-progress-4': {
      title: 'Reflect and Progress',
      description: 'Reflection + discussion on video storytelling and prompting.',
      duration: '5–8 minutes',
      difficulty: 'Easy',
      instructions: [
        'Discussion prompts:',
        '“What happened in the video?”',
        '“What details did you give the AI that helped it make your video look right?”',
        '“Do you think it helped that your teacher used images to prompt the video? Why?”',
        '“If you could change one sentence in your prompt to make your video better, what would you change and why?”',
      ],
    },
    'reflect-and-progress-5': {
      title: 'Reflect and Progress',
      description: 'Reflection + wrap-up discussion.',
      duration: '5–10 minutes',
      difficulty: 'Easy',
      instructions: [
        'Use the standard wrap-up: “What did we build today?” “What was hard?” “What will we try next?”',
      ],
    },
    'reflect-and-progress-6': {
      title: 'Reflect and Progress',
      description: 'Reflection + listening celebration.',
      duration: '5 minutes',
      difficulty: 'Easy',
      instructions: [
        'Have 1 student per team share one thing they liked about their song.',
        'Ask: “What would you change or add next time?”',
      ],
    },
    'reflect-and-progress-7': {
      title: 'Reflect and Progress',
      description: 'Reflection + discussion on game goals and mechanics.',
      duration: '10 minutes',
      difficulty: 'Easy',
      instructions: [
        'Have each team share: “How do you play the game?”',
        'Ask: “What details helped the AI make your game work well?”',
        'Ask: “What would you change in one sentence and why?”',
      ],
    },

    // Class 1 — hand-converted do/say so a stranger can teach from the screen
    'cwai-1-welcome': {
      title: 'Welcome & Overview',
      missionType: 'welcome',
      priority: 'core',
      plannedMinutes: 5,
      prompt: 'Welcome to Create Worlds with AI. Today we invent characters — and your words make them real.',
      duration: '5 minutes',
      script: [
        {
          kind: 'say',
          text: 'Welcome to Create Worlds with AI. Today we invent characters — and your words make them real.',
        },
        {
          kind: 'say',
          text: 'You’ll work in teams. One person writes ideas on paper. One person helps type the final prompt. I run the AI so everything stays safe for class.',
        },
        {
          kind: 'say',
          text: 'Our goal today: every team leaves with a character image that came from your words.',
        },
        {
          kind: 'do',
          text: 'Point at the Character Creation sheet on the board (or hold up a blank page) so kids know what they’ll fill in.',
        },
      ],
    },
    'cwai-1-character-l1': {
      title: 'Character L1: Describe Your Character',
      missionType: 'activity',
      priority: 'core',
      plannedMinutes: 5,
      prompt:
        'Draw your character. What animal are they? What’s their name? What are they doing? How do they feel?',
      subtitle: 'Write in complete sentences — those words become your character.',
      duration: '5 minutes',
      worksheet: CHARACTER_CREATION_WORKSHEET,
      script: [
        { kind: 'do', text: 'Make sure every team has the Character Creation sheet (or blank paper) and pencils.' },
        {
          kind: 'say',
          text: 'Fill in animal, name, what they’re doing, and how they feel. Use complete sentences.',
        },
        {
          kind: 'say',
          text: 'Here’s an example: Make an image of my character. My character is a zebra named Zed. Zed is dancing and happy.',
        },
        {
          kind: 'do',
          text: 'Circulate. Pair quieter kids with talkers if a team is stuck. Collect finished prompts before you type anything into Gemini.',
        },
        {
          kind: 'do',
          text: 'For each team: paste their prompt into Gemini, preview the image yourself, then show it. Save as Artifact 1.',
        },
      ],
    },
    'cwai-1-setup': {
      title: 'Character Creators Setup',
      missionType: 'activity',
      priority: 'core',
      plannedMinutes: 10,
      prompt: 'Team up. One Writer. One Prompter. Your words will build a character together.',
      duration: '10 minutes',
      worksheet: CHARACTER_CREATION_WORKSHEET,
      script: [
        { kind: 'do', text: 'Group students into pairs (or existing teams of two).' },
        {
          kind: 'say',
          text: 'Each team picks a Writer and a Prompter. Writer holds the pencil and paper. Prompter helps turn ideas into the final sentence.',
        },
        { kind: 'do', text: 'Hand out one Character Creation sheet (or blank paper) and pencils per team.' },
        {
          kind: 'say',
          text: 'Writers, get ready. We’re about to invent characters together — keep your ideas on the page until I call you up.',
        },
        {
          kind: 'do',
          text: 'On your device: Gemini ready, Canva board open with team names. Project only the Canva board when you share images.',
        },
      ],
    },
    'cwai-1-character-l2': {
      title: 'Character L2: Dress to Impress',
      missionType: 'activity',
      priority: 'flex',
      plannedMinutes: 5,
      prompt: 'Dress your character. What hat, shoes, or special materials do they wear?',
      duration: '5 minutes',
      script: [
        {
          kind: 'say',
          text: 'Time to dress your character. Add a hat, shoes, or special materials — still in complete sentences.',
        },
        {
          kind: 'say',
          text: 'Example: Zed is wearing a blue cotton bucket hat and green rubber sneakers.',
        },
        {
          kind: 'do',
          text: 'Collect revised prompts. Generate in Gemini, preview, then show. Save as Artifact 2. If a team finishes early, ask for one more detail (color or material).',
        },
      ],
    },
    'cwai-1-character-l3': {
      title: 'Character L3: Secret Items',
      missionType: 'activity',
      priority: 'flex',
      plannedMinutes: 5,
      prompt: 'Give your character a secret item. Where is it — left, right, on top, or behind?',
      duration: '5 minutes',
      script: [
        {
          kind: 'say',
          text: 'Give your character a secret item. Say exactly where it is — left, right, on top of, or behind.',
        },
        {
          kind: 'say',
          text: 'Example: Zed has a paintbrush in his left hand.',
        },
        {
          kind: 'do',
          text: 'Generate, preview, show. Save as Artifact 3. If Gemini ignores placement, start a fresh chat with the last good image.',
        },
      ],
    },
    'cwai-1-capture': {
      title: 'Photograph team drawings',
      missionType: 'capture',
      priority: 'core',
      plannedMinutes: 5,
      prompt: 'Hold your drawing up — we’re photographing every team’s work.',
      duration: '5 minutes',
      script: [
        {
          kind: 'say',
          text: 'Hold your drawing up so I can see the whole page. We’re photographing every team’s work for your portfolio.',
        },
        {
          kind: 'do',
          text: 'Use Upload photos on each team chip (or your phone). Get one clear photo of each team’s Character Creation sheet or drawing.',
        },
        {
          kind: 'say',
          text: 'Great — these photos are how we remember what you made today.',
        },
      ],
    },

    // Class 2
    'cwai-2-review': {
      title: 'Review (World Builders)',
      description: 'Quick recap and set today’s goal: build a world for your characters using AI.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: ['Projector or large display'],
      instructions: [
        'Discussion:',
        '“Who did you work with?”',
        '“What did you create last class?”',
        '“What did you learn last week?”',
        '“What is the one thing already in our portfolio?” (Characters)',
        'Introduce the goal: “Today you’ll create a world for your characters using AI — but make sure to use complete sentences!”',
        'Review safety workflow (writing → teacher review → Gemini → reflection).',
      ],
    },
    'cwai-2-world-l1': {
      title: 'World L1: Describe Your Landscape',
      description:
        'Students choose weather, land, and mood and name their landscape. Teacher generates the base world image.',
      duration: '10 minutes',
      difficulty: 'Easy',
      materials: [
        '“World Creation” templates (project on the board)',
        'Pencils + blank paper',
        '3–6 classroom devices',
        { label: 'Gemini', link: GEMINI_LINK },
        {
          label: 'Artifacts board (Canva) — Class 2',
          link: 'https://www.canva.com/design/DAG2DP2lTTw/LXLZcFdf-0rqNo8wBvN-iw/edit?utm_content=DAG2DP2lTTw&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton',
        },
      ],
      instructions: [
        'Setup (teacher):',
        '- Log into Gemini.',
        '- Open a new chat for each team and set chats to nano-banana image creation.',
        '- Hint: if a chat is giving bad results, start a new chat and copy/paste the last good image to start over.',
        '- Open and project the Canva whiteboard (team names + student names).',
        '- Copy/paste images from Gemini into the Canva doc as you go.',
        '- Hint: copy/paste these images back to Gemini alongside written prompts for better results.',
        '',
        'Running the activity:',
        '- Group students into pairs of two (new pairs if you want).',
        '- Choose WRITER and PROMPTER roles (writer on paper, prompter in OG Create).',
        '- Launch OG Create for students and monitor prompts in teacher toolbelt.',
        '',
        'Level 1 prompt (example):',
        '“Make an image of my landscape called Aria. Aria is a windy, spooky desert.”',
        '✅ Artifact 1: Base World image.',
      ],
    },
    'cwai-2-world-l2': {
      title: 'World L2: Plants & Animals',
      description:
        'Add flora and fauna (and optionally a landform) to expand the world with life and detail.',
      duration: '10 minutes',
      difficulty: 'Easy',
      materials: [{ label: 'Gemini', link: GEMINI_LINK }],
      instructions: [
        'Add flora and fauna.',
        'Example: “Add capybaras, cactuses, cherry trees, and a valley to Aria.”',
        '✅ Artifact 2: Expanded World with life and detail.',
      ],
    },
    'cwai-2-world-l3': {
      title: 'World L3: Buildings',
      description:
        'Add a structure with descriptive adjectives and clear position language (left/right/foreground/background).',
      duration: '10 minutes',
      difficulty: 'Easy',
      materials: [{ label: 'Gemini', link: GEMINI_LINK }],
      instructions: [
        'Add a structure with descriptive adjectives and position.',
        'Example: “Add a large, round, orange, creaky house to the right side of Aria.”',
        '✅ Artifact 3: Full world with environment and structures.',
      ],
    },
    'cwai-2-world-l4': {
      title: 'World L4: Make a Friend (Collab)',
      description:
        'Partner up and combine characters in one world using image + written prompting.',
      duration: '8–10 minutes',
      difficulty: 'Easy',
      materials: [{ label: 'Gemini', link: GEMINI_LINK }],
      instructions: [
        'Partner up and combine characters in one world.',
        'Written prompt example:',
        '“Add the images of Zed and Jill to the landscape of Aria. Zed and Jill are dancing on the roof.”',
        'Image prompt: paste three images into the chat:',
        'A) character 1',
        'B) character 2',
        'C) landscape',
        '✅ Artifact 4: Collaborative image showing two characters together.',
        'Level 5 (Replay Levels): allow students to replay levels to make new characters/accessories/worlds.',
      ],
    },

    // Class 3
    'cwai-3-review': {
      title: 'Review (Story Tellers)',
      description: 'Quick recap and set today’s goal: create a story using complete sentences.',
      duration: '5 minutes',
      difficulty: 'Easy',
      instructions: [
        'Discussion:',
        '“Who did you work with last class?”',
        '“What worlds and characters did you create so far?”',
        '“What did you learn last week?”',
        '“What are the 2 things already in our portfolio?” (Characters, World)',
        'Introduce the goal: “Today you’ll create a story for your characters and world using AI — but make sure to use complete sentences!”',
        'Review safety workflow (writing → teacher review → Gemini → reflection).',
      ],
    },
    'cwai-3-warmup-class-story': {
      title: 'Warm-Up: Whole-Class Story (Context Demo)',
      description:
        'Make a class character and class world. Then show the same story prompt with and without context to teach pre-prompting.',
      duration: '10 minutes',
      difficulty: 'Easy',
      materials: [{ label: 'Gemini', link: GEMINI_LINK }],
      instructions: [
        'Tell students we will be making a class character and class world as context for a class story.',
        'Take verbal suggestions from students to create a class character; name it yourself; input prompt into Gemini.',
        'While it generates, take suggestions to create a class world; input prompt into Gemini.',
        'Explain: the character and world are now context for the story.',
        'Take suggestions for an introduction scene + a piece of dialogue the character says.',
        'Generate once WITH context (paste character + world images and include the pre-prompt text).',
        'Generate once WITHOUT any context (no images, no pre-prompt).',
        'Ask: “Why are the pictures different?” “What happens if you don’t give enough detail?”',
        'Explain pre-prompting: giving the AI the context it needs.',
        'Repeat for a conflict slide and a resolution slide. Then it’s their turn.',
      ],
    },
    'cwai-3-beginning': {
      title: 'Story Panel 1: Beginning',
      description:
        'Level 1: Context — Beginning (Introduction). Students write a full paragraph prompt and include dialogue.',
      duration: '10 minutes',
      difficulty: 'Medium',
      materials: [{ label: 'Gemini', link: GEMINI_LINK }],
      instructions: [
        'Important: make sure students include a pre-prompt with their character and world.',
        'If they mention their character and world, add screenshots of both images to the prompt.',
        'If they don’t, do not include images—use it as a teaching moment about missing context.',
        '',
        'Example preprompt:',
        '“Make an image of my character Bob. Bob is a yellow giraffe who is happy. Bob is in the desert. The desert is called Balia and has a big house and cactuses all around.”',
        'Example story prompt:',
        '“In the beginning Bob is sitting down on his lawn chair outside his house. Make a dialogue bubble of Bob saying ‘Hello my name is Bob’”',
        '',
        'Together (full prompt example):',
        '“Make an image of my character Bob. Bob is a yellow giraffe who is happy. Bob is in the desert. The desert is called Balia and has a big house and cactuses all around. In the beginning Bob is sitting down on his lawn chair outside his house. Make a dialogue bubble of Bob saying ‘Hello my name is Bob’”',
        '✅ Artifact 1: Beginning (Introduction) story panel.',
      ],
    },
    'cwai-3-middle': {
      title: 'Story Panel 2: Middle (Conflict)',
      description:
        'Level 2: Middle (Conflict). Add a conflict and update the dialogue.',
      duration: '10 minutes',
      difficulty: 'Medium',
      materials: [{ label: 'Gemini', link: GEMINI_LINK }],
      instructions: [
        'Example story prompt:',
        '“In the middle, Bob it starts to rain. Make a dialogue bubble of Bob saying ‘Oh no!! It’s raining!’”',
        'Together (full prompt example):',
        '“Make an image of my character Bob. Bob is a yellow giraffe who is happy. Bob is in the desert. The desert is called Balia and has a big house and cactuses all around. In the middle, Bob it starts to rain. Make a dialogue bubble of Bob saying ‘Oh no!! It’s raining!’”',
        '✅ Artifact 2: Middle (conflict) story panel.',
      ],
    },
    'cwai-3-end': {
      title: 'Story Panel 3: End (Resolution)',
      description:
        'Level 3: End (Resolution). Add a resolution and update dialogue.',
      duration: '10 minutes',
      difficulty: 'Medium',
      materials: [{ label: 'Gemini', link: GEMINI_LINK }],
      instructions: [
        'Example story prompt:',
        '“In the end Bob gets a tall umbrella and goes outside. Make a dialogue bubble of Bob saying ‘Ah, much better’”',
        'Together (full prompt example):',
        '“Make an image of my character Bob. Bob is a yellow giraffe who is happy. Bob is in the desert. The desert is called Balia and has a big house and cactuses all around. In the end Bob gets a tall umbrella and goes outside. Make a dialogue bubble of Bob saying ‘Ah, much better’”',
        '✅ Artifact 3: End (resolution) story panel.',
        'Level 4 (Add more panels): allow students to add more story panels between beginning/middle/end.',
      ],
    },

    // Class 4
    'cwai-4-watch-example': {
      title: 'Warm-Up: Watch Bao + Discussion',
      description:
        'Watch the short version of Bao and discuss story, style, mood, and techniques.',
      duration: '5–10 minutes',
      difficulty: 'Easy',
      materials: [
        'Projector/speakers',
        { label: 'Bao (YouTube)', link: 'https://www.youtube.com/watch?v=f5CcgFTO274' },
      ],
      instructions: [
        'Watch Bao together.',
        'Discussion prompts:',
        '“Who were the characters?”',
        '“What was the world?”',
        '“What happened in the beginning? The middle? The end?”',
        '“What was the style of the film?” (e.g., 3D animated)',
        '“What was the mood of the film?”',
        '“What techniques for filming were used?”',
        'Goal: get students thinking about storytelling through video and film.',
      ],
    },
    'cwai-4-write-video-prompt': {
      title: 'Make your film: Styles, Moods & Techniques',
      description:
        'Students plan style, mood, and technique so the video prompt is specific and controlled.',
      duration: '10 minutes',
      difficulty: 'Medium',
      materials: [
        '“Video Producers” templates projected on the board',
        'Pencils + blank paper',
        '3–6 classroom devices',
        'Teacher login(s) for OpenAI (Sora) or Google (Veo)',
      ],
      instructions: [
        'Setup (teacher):',
        '- Log into Sora (or Veo).',
        '- Open a new tab for each team.',
        '- Hint: if a tab is giving bad results, start a new tab.',
        '- Be ready to mute and preview videos on your device before playing for the class.',
        '- After class you will download videos and upload to the Google Drive folder.',
        '',
        'Running the activity:',
        '- Students write prompts on paper first; prompters submit in OG Create; teacher reviews and pastes into Sora.',
        '- Important: if students mention their character and world, include screenshots as context. If not, do not add context images (teaching moment).',
        '',
        'Level 3 (style/mood/technique):',
        '- Style example: “Make the style an anime cartoon style.”',
        '- Mood example: “Make the mood happy and then the rain comes and it is sad.”',
        '- Technique example: “Make the lighting happy and yellow. Make the camera move fast.”',
      ],
    },
    'cwai-4-generate-video': {
      title: 'Put it all together (Full Video Prompt)',
      description:
        'Combine context (character + world + story) with style, mood, and technique into one full prompt and generate.',
      duration: '10 minutes',
      difficulty: 'Medium',
      materials: ['Video generation tool (teacher account)'],
      instructions: [
        'Level 1 (context): character + world preprompt.',
        'Level 2 (story): beginning + middle + end in full sentences.',
        'Level 3 (style/mood/technique): add style, mood arc, lighting/camera technique.',
        '',
        'Full prompt example:',
        '“Make a video of my character Bob. Bob is a yellow giraffe who is happy. Bob is in the desert. The desert is called Balia and has a big house and cactuses all around. In the beginning Bob is sitting down on his lawn chair outside his house and says ‘Hello my name is Bob.’ Then it starts to rain. Bob says ‘Oh no!! It’s raining!’ Finally Bob gets a tall umbrella and goes outside. Bob says ‘Ah, much better’. Make the style an anime cartoon style. Make the mood happy and then the rain comes and it is sad. Make the lighting happy and yellow. Make the camera move fast.”',
        'Enter the full prompt and upload a screenshot of their character + world if supported.',
      ],
    },
    'cwai-4-download-upload': {
      title: 'Download + Upload Artifact',
      description:
        'After class, download the videos and upload them to the shared Google Drive folder with consistent naming.',
      duration: 'After class',
      difficulty: 'Easy',
      materials: [
        {
          label: 'Google Drive folder (videos)',
          link: 'https://drive.google.com/drive/folders/1kY3c6WUBsXmS14WfcyfHEgRGN5gm-qv3?usp=sharing',
        },
      ],
      instructions: [
        'In Sora: open the three dots next to the video and click Download.',
        'Upload the video to the class’s folder in Google Drive (create one if needed).',
        'Name the file with the school and the names of the kids on the team.',
        'Example: “Baker Elementary Jasper & Sarah.mp4”',
        'Delete the videos from your computer after upload.',
      ],
    },

    // Class 5
    'cwai-5-review': {
      title: 'Introduction Activity (10 min)',
      description: 'Review the project from last class by showing student work and discussing it.',
      duration: '10 minutes',
      difficulty: 'Easy',
      instructions: [
        'Show each of the projects the students made.',
        'Discussion questions:',
        '“Who did you work with?”',
        '“What did you create?”',
        '“What did you like about it? What did you not like?”',
        '“What would you change next time?”',
      ],
    },
    'cwai-5-teams-fave-characters': {
      title: 'Teams and Animations (10 min)',
      description: 'Warm up by connecting animation to characters students already know.',
      duration: '10 minutes',
      difficulty: 'Easy',
      instructions: [
        'Give students 1 minute to brainstorm favorite animated movies in teams of 2.',
        'Ask: “Who is your favorite character from that movie?”',
        'Pull up short videos/images of characters they mention and share them.',
      ],
    },
    'cwai-5-class-demo': {
      title: 'Full Class Character Animation (25 min)',
      description:
        'No individual devices. Turn one student character into a 3D animation (Gemini pose cleanup → Meshy model → animation).',
      duration: '25 minutes',
      difficulty: 'Medium',
      materials: [
        { label: 'Meshy', link: MESHY_LINK },
        { label: 'Gemini', link: GEMINI_LINK },
      ],
      instructions: [
        'Open Meshy and log in.',
        'Open Gemini and log in.',
        '',
        'GEMINI — Get a clean character pose:',
        '- Choose a student character at random (use a random number generator).',
        '- Screenshot the character and paste into Gemini.',
        '- Choose a prompt:',
        '  - Humanoid: “Make this character with a blank background and a t pose for 3d image generation”',
        '  - Quadruped: “Make this character with a blank background and a quadruped pose for 3d image generation”',
        '- Explain humanoid vs quadruped and what “pose” means.',
        '- Copy the resulting image.',
        '',
        'MESHY — Get 3D model (credits):',
        '- Navigate to Image → 3D Model.',
        '- Paste the image (⌘V / Ctrl+V).',
        '- Click Generate.',
        '- Click “Add texture” (explain: texture = colors).',
        '',
        'MESHY — Make animation:',
        '- Remesh with less shapes (explain mesh triangles; too many shapes is hard to animate).',
        '- Click Animate (it may prompt you to remesh).',
        '- Choose humanoid or quadruped.',
        '- Have students come up and move circles to correct anatomical places.',
        '- Click animate and try multiple animations (make it dance!).',
      ],
    },
    'cwai-5-teams-tpose': {
      title: 'Group Character Animations (Cliff Hanger) (15 min)',
      description:
        'Teams each generate a clean blank-background T-pose character for the teacher to animate before next class.',
      duration: '15 minutes',
      difficulty: 'Easy',
      materials: [{ label: 'Gemini', link: GEMINI_LINK }],
      instructions: [
        'Split students into teams of 2.',
        'Open OG Create and have them open it too.',
        'Tell them to create a prompt that will make a character with a blank background and a T-pose.',
        'Generate their characters as they come up.',
        'Make sure each team completes a character by the end.',
        'If they finish early, have them come up with a friend for their character.',
      ],
    },
    'cwai-5-extra-fast3d': {
      title: 'Extra Activity: Hands on Generation (15 min)',
      description:
        'Optional hands-on: teams prompt 2 items from their world and generate 3D results.',
      duration: '15 minutes',
      difficulty: 'Easy',
      materials: [{ label: 'fast3d', link: FAST3D_LINK }],
      instructions: [
        'Open fast3d.io.',
        'Put students in teams of 2.',
        'Have them prompt 2 items from their world to generate.',
        'Be strict with sentence structure: only full sentences are allowed.',
        'Generate and show results.',
      ],
    },

    // Class 6
    'cwai-6-welcome': {
      title: 'Welcome & Overview (Musical Composers)',
      description:
        'Introduce Suno + AI music and the goal: write complete-sentence prompts and lyrics for a song about your character/world/story.',
      duration: '5 minutes',
      difficulty: 'Easy',
      materials: [
        '“Musical Composers” templates projected on the board',
        'Pencils, coloring materials',
        '3–6 classroom devices',
        'Teacher login(s) for Suno',
        'Projector or large display',
        'Example song',
        { label: 'Suno', link: SUNO_LINK },
      ],
      instructions: [
        'Play welcome music using the music tab from the teacher’s toolbelt.',
        'Introduce the goal: “Today you’ll create a song about your character, world and story using AI — but you must use complete sentences!”',
        'Set ground rules for safety and behavior.',
        'Review safety workflow (writing → teacher review → Suno → reflection).',
        'Safety prompt to paste into Suno:',
        SAFETY_MUSIC_PROMPT,
      ],
    },
    'cwai-6-level1-about': {
      title: 'Level 1 — Simple: What is it about?',
      description: 'Students describe the character, story, and world behind their song.',
      duration: '10 minutes',
      difficulty: 'Easy',
      instructions: [
        'Students complete the “about” template in full sentences.',
        'Example: “My song is about Bob the Giraffe in the world of IceCreamrville where everything is made out of ice cream.”',
        'No artifact yet—move to lyrics.',
      ],
    },
    'cwai-6-level2-lyrics': {
      title: 'Level 2 — Custom: Lyrics',
      description: 'Students add lyrics (words for the song to sing).',
      duration: '10 minutes',
      difficulty: 'Medium',
      instructions: [
        'Students write a few lyric lines.',
        'In Suno, use the orange button to extend lyrics into a full song.',
        'Example: “Add some lyrics that say: Because I’m happy! Clap along if you feel like a room without a roof!”',
        'No artifact yet—move to style.',
      ],
    },
    'cwai-6-level3-style': {
      title: 'Level 3 — Custom: Style',
      description: 'Students choose genres and instruments for the song.',
      duration: '10 minutes',
      difficulty: 'Medium',
      instructions: [
        'Students pick genres + instruments.',
        'Example: “Make a song in the style of rock, Irish folk and Mongolian throat singing. Add the instruments: trumpet, violin, tuba and accordion.”',
        'Then combine Level 1 + 2 + 3 into one final prompt paragraph.',
      ],
    },
    'cwai-6-generate-song': {
      title: 'Generate a Song (Suno)',
      description:
        'Teacher pastes the safety prompt and the team’s final combined prompt into Suno. Generate a 1+ minute song.',
      duration: '15 minutes',
      difficulty: 'Medium',
      materials: [{ label: 'Suno', link: SUNO_LINK }],
      instructions: [
        'Setup (teacher):',
        '- Log into Suno.',
        '- Open a new tab for each team and paste the song safety prompt.',
        '- Hint: if a tab is giving bad results, start a new tab.',
        '- Play audio as you go; save audio files into Google Drive at the end.',
        '',
        'Generate:',
        `1) Paste the safety prompt:\n${SAFETY_MUSIC_PROMPT}`,
        '2) Paste the final prompt paragraph (Level 1 + Level 2 + Level 3 combined).',
        '3) If using Suno “Lyrics” field, put lyrics there; the rest goes in the general prompt.',
        '4) Generate and pick the best version.',
        '✅ Artifact: Full 1+ minute song generation.',
      ],
    },
    'cwai-6-download-upload': {
      title: 'Download + Upload MP3',
      description:
        'Download mp3s and upload to the shared Google Drive folder with consistent naming.',
      duration: 'After class',
      difficulty: 'Easy',
      materials: [
        {
          label: 'Google Drive folder (audio + video)',
          link: 'https://drive.google.com/drive/folders/1kY3c6WUBsXmS14WfcyfHEgRGN5gm-qv3?usp=sharing',
        },
      ],
      instructions: [
        'In Suno: click the three dots next to the song and click Download → mp3.',
        'Click “download anyways” if prompted (not using commercially).',
        'Upload mp3 files to the Google Drive class folder.',
        'Name the song with the school and the team of kids.',
        'Example: “Baker Elementary Jasper & Sarah”',
        'Delete the songs from your computer after upload.',
      ],
    },

    // Class 7
    'cwai-7-generate-game': {
      title: 'Generate a Game (Rosebud)',
      description:
        'Teacher pastes the student prompt into Rosebud.ai and generates a simple 2D desktop game.',
      duration: '10–15 minutes',
      difficulty: 'Medium',
      materials: [{ label: 'Rosebud', link: ROSEBUD_LINK }],
      instructions: [
        'Setup (teacher):',
        '- Log into Rosebud.ai.',
        '- Open a new tab for each team.',
        '- Hint: if a tab is giving bad results, start a new tab.',
        '- Upload an image of their character from a past class (if supported).',
        '- Choose 2D → Desktop and wait.',
        '- If it fails, ask it to fix the code or try again in a new tab.',
        '- Generations are limited: consider doing 1–2 teams today, then return to the rest next class.',
      ],
    },
    'cwai-7-review': {
      title: 'Review (Game Developers)',
      description: 'Quick recap of the portfolio so far and today’s mission: build a playable game.',
      duration: '5 minutes',
      difficulty: 'Easy',
      instructions: [
        'Discussion:',
        '“Who did you work with last class?”',
        '“What worlds and characters did you create so far?”',
        '“What did you learn last week?”',
        '“What are the 6 things already in our portfolio?” (Characters, World, Story, Trailer, 3D animated model, Song)',
        'Introduce the goal: “Today you’ll create a game full of your characters, story and world using AI — but you must use complete sentences!”',
      ],
    },
    'cwai-7-analyze-games': {
      title: 'Warm-Up: Analyze Overture Music Games',
      description:
        'Play and analyze games to learn what makes good mechanics: goals, progress, win/lose, and player actions.',
      duration: '20 minutes',
      difficulty: 'Easy',
      materials: [{ label: 'Overture', link: OVERTURE_LINK }],
      instructions: [
        'Play 1 round of Rocket Race.',
        'Then choose 1–3 games and call student volunteers up to play:',
        '- Rhythm Rally (1–4 players)',
        '- Yeet the Scoop (1 player)',
        '- Scale the Tower (1 player)',
        '- Chordstruction (1 player)',
        '',
        'After each game, discuss:',
        'AI curriculum questions:',
        '- “What characters are in the game?”',
        '- “How would you describe the world?”',
        '- “What is the story behind the game? Does it have a story?”',
        '- “What video styles, moods and techniques do you see used?”',
        '- “How do the animations make you feel?”',
        '- “What musical instruments and genres do you hear?”',
        '',
        'Game mechanic questions:',
        '- “How many people are playing?”',
        '- “How many teams are there?”',
        '- “What is the goal?”',
        '- “What do you do to achieve the goal?”',
        '- “Is there a way to win or lose (end state)?”',
        '- “How does the game show progress as you do those things?”',
      ],
    },
    'cwai-7-level1-context': {
      title: 'Level 1: Context (Character + World)',
      description: 'Students create the context for their character and world in full sentences.',
      duration: '10 minutes',
      difficulty: 'Easy',
      instructions: [
        'Preprompt example:',
        '“The main player is Bob. Bob is a yellow giraffe who is happy. Bob is in the desert. The desert is called Balia and has a big house and cactuses all around.”',
      ],
    },
    'cwai-7-level2-problem-goal': {
      title: 'Level 2: Problem & Goal',
      description: 'Add a problem (challenge) and a goal/end condition.',
      duration: '10 minutes',
      difficulty: 'Medium',
      instructions: [
        'Problem prompt example:',
        '“Bob is trying to not get hit by the rain. Rain drops are falling everywhere and if he gets hit by one he loses life.”',
        'Goal prompt example:',
        '“The game ends once Bob has gotten an umbrella and dodged 30 raindrops.”',
      ],
    },
    'cwai-7-level3-put-together': {
      title: 'Level 3: Put it all together',
      description: 'Combine context + problem + goal into one full game prompt paragraph.',
      duration: '10 minutes',
      difficulty: 'Medium',
      instructions: [
        'Full prompt example:',
        '“The main player is Bob. Bob is a yellow giraffe who is happy. Bob is in the desert. The desert is called Balia and has a big house and cactuses all around. Bob is trying to not get hit by the rain. Rain drops are falling everywhere and if he gets hit by one he loses life. The game ends once Bob has gotten an umbrella and dodged 30 raindrops.”',
      ],
    },
    'cwai-7-share-record': {
      title: 'Artifacts: Share link + Screen Recording',
      description:
        'After class, capture gameplay and upload to the shared Drive. Also copy the share link into Canva.',
      duration: 'After class',
      difficulty: 'Easy',
      materials: [
        {
          label: 'Google Drive folder (screen recordings)',
          link: 'https://drive.google.com/drive/folders/1kY3c6WUBsXmS14WfcyfHEgRGN5gm-qv3?usp=sharing',
        },
      ],
      instructions: [
        'Deliverables:',
        '1) Share the game and copy the link. Paste the link in Canva.',
        '2) Take a screen recording of gameplay.',
        'Apple: Command + Shift + 5',
        'Windows: (decide your screen recorder)',
        'Download → upload to the class folder.',
        'Name the file with the school and the names of the kids on the team.',
        'Example: “Baker Elementary Jasper & Sarah.mp4”',
        'Delete the file from your computer after upload.',
      ],
    },
  };

  const entry = entries[missionId];
  if (entry) return { ...baseActivity(missionId), ...entry };

  // Generic fallback so UI never crashes even if content is missing.
  return {
    ...baseActivity(missionId),
    title: `Create Worlds with AI: ${missionId}`,
    description: 'Activity details coming soon. Use the mission text as your guide and follow the teacher-filtered workflow.',
    duration: '5–15 minutes',
    difficulty: 'Easy',
    materials: [{ label: 'Overture', link: OVERTURE_LINK }],
    instructions: [
      'Have students write prompts on paper first (full sentences).',
      'Teacher reviews prompts for safety and clarity.',
      'Teacher generates in the chosen tool and previews results before sharing.',
      'Students revise and iterate.',
    ],
  };
};

export const aiCreateWorlds2026Adapter: CurriculumAdapter = {
  id: 'ai-2026-create-worlds',
  levels: LEVELS,
  getLevelById: (id: string) => LEVELS.find(l => l.id === id),
  getLevelIndex: (id: string) => Math.max(0, indexById(id)),
  getLevelStatus,
  getShortLevelName,
  getMissionActivity,
};
